import React from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';
import { IGetCareRecordRes } from '@/types/api/care-record/getCareRecord';
import { color, TableLinkText } from '@/styles';
import { mathRound } from '@/utils/dataFormatUtil';
import { POSTURES } from '@/constants/activityStatus';
import { useFetchResidentCareRecord } from '@/hooks/api/careRecord/useFetchResidentCareRecord';
import { useFetchDailyCareRecord } from '@/hooks/api/traversal/useFetchDailyCareRecord';
import { useFetchResident } from '@/hooks/api/resident/useFetchResident';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useCareRecordCsvExporter } from '@/hooks/useCareRecordCsvExporter';
import { CsvDownloadButton } from '@/components/analysis/CsvDownloadButton';

interface Props {
  nursingHomeId: string;
  residentId: string;
}

interface ITotalTable {
  totalUrineVolume: number; // 合計尿量(ml)
  totalUrineCount: number; // 合計排尿回数
  averageRuv: number; // 平均残尿量(ml)
  totalRuvCount: number; // 合計残尿回数
  leakCount: number; // 尿漏れ回数
  urgencyCount: number; // 切迫感回数
}

interface ICareRecordTable {
  careRecordId: number;
  timestamp: string;
  urineVolume: number | null; // padUrineVolumeとtoiletVolumeを足した値（未記録ならnull）
  isLeak: boolean;
  isUrgency: boolean;
  accumulation: number;
  posture: number;
  ruv: number | null;
  memo: string;
}

const TdCenter = css`
  text-align: center;
`;

const Body = css`
  min-width: 600px;
  padding: 24px;
  min-height: 40vh;
`;

const UpperContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DatePickerContainer = css`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TableContainer = css`
  margin-bottom: 24px;
`;

const Th = (bgColor: string = '#FFFFFF') => css`
  border: 1px solid ${color.border_gray};
  padding: 8px;
  text-align: left;
  font-weight: normal;
  background-color: ${bgColor};
`;

const Td = (bgColor: string = '#FFFFFF') => css`
  border: 1px solid ${color.border_gray};
  padding: 8px;
  text-align: left;
  font-weight: normal;
  background-color: ${bgColor};
`;

const CellGap = css`
  min-width: 40px;
  border: none;
  background-color: transparent;
`;

const SummaryTable = css`
  border-collapse: collapse;
  margin: 12px 0;
`;

const TableSpacer = css`
  height: 12px;
  padding: 0;
  border: none;
  background-color: transparent;
`;

const DiaryTable = css`
  width: 100%;
  min-width: 800px;
  table-layout: fixed;
`;

const DiaryTableScroll = css`
  width: 100%;
  overflow-x: auto;
`;

const TextCenter = css`
  display: flex;
  justify-content: center;
  white-space: nowrap;
  padding: 0;
`;

const MemoCell = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Icon = css`
  width: 24px;
  height: 24px;
  display: block;
  margin: 0 auto;
`;

// テーブルのセルの横幅を左から順に定義
const CellWidth = [
  { width: '144px' },
  { width: '96px' },
  { width: '96px' },
  { width: '88px' },
  { width: '72px' },
  { width: '72px' },
  { width: '80px' },
  { width: 'auto' },
  { width: '48px' },
];

export const ResidentDetailDiary: React.FC<Props> = ({ nursingHomeId, residentId }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const initialStartDate = dayjs().set('hour', 0).set('minute', 0).set('second', 0).utc();
  const initialEndDate = initialStartDate.add(1, 'day');

  const { residentCareRecord, fetchResidentCareRecord } = useFetchResidentCareRecord(
    true,
    +residentId,
    initialStartDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    initialEndDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    'asc'
  );
  const { fetchDailyCareRecord } = useFetchDailyCareRecord(false);
  const { resident } = useFetchResident(true, +residentId);
  const { exportCareRecordCsv } = useCareRecordCsvExporter();
  const [onSend, setOnSend] = React.useState<boolean>(false);
  const [range, setRange] = React.useState<{ start: Dayjs; end: Dayjs }>({
    start: initialStartDate,
    end: initialEndDate,
  });

  const handleChangeStart = React.useCallback((start) => {
    setRange((prev) => {
      return {
        ...prev,
        start: dayjs(start).local().utc(),
        isCreate: false,
      };
    });
    fetchResidentCareRecord(
      +residentId,
      dayjs(start).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      dayjs(range.end).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      'asc'
    );
  }, []);

  const handleChangeEnd = React.useCallback((end) => {
    setRange((prev) => {
      return {
        ...prev,
        end: dayjs(end).local().utc(),
        isCreate: false,
      };
    });
    fetchResidentCareRecord(
      +residentId,
      dayjs(range.start).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      dayjs(end).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      'asc'
    );
  }, []);

  const handleClickEdit = (id: number) => () => {
    history.push(`/nursinghome/${nursingHomeId}/residents/${residentId}/care-record/${id}/edit`);
  };

  const formatNumberOrBlank = (value: number | null | undefined) => {
    return value == null ? '' : value;
  };

  const U2P_POSTURE_INFO_BY_ID = new Map(
    POSTURES.u2p.map((p) => [
      p.id,
      {
        name: p.name,
        iconPath: p.iconPath,
      },
    ])
  );

  const renderPostureIcon = (posture: number | null | undefined) => {
    if (posture == null) return '';
    const info = U2P_POSTURE_INFO_BY_ID.get(posture);
    if (!info) return '';
    return <img src={info.iconPath} alt={info.name} css={Icon} />;
  };

  const createTableRecord = (records: IGetCareRecordRes[]) => {
    return records.map((v) => {
      const hasUrineVolume = v.padUrineVolume != null || v.toiletVolume != null;
      const urineVolume = hasUrineVolume ? (v.padUrineVolume ?? 0) + (v.toiletVolume ?? 0) : null;
      return {
        careRecordId: v.id,
        timestamp: dayjs(v.timestamp).local().format('YYYY/M/D H:mm'),
        urineVolume,
        isLeak: v.urineLeakage,
        isUrgency: v.isUrgency,
        accumulation: v.accumulation,
        posture: v.posture,
        ruv: v.ruv ?? null,
        memo: v.memo,
      };
    });
  };

  const createRecordTotal = (records: IGetCareRecordRes[]) => {
    let totalUrineVolume = 0;
    let totalUrineCount = 0;
    let totalRuv = 0;
    let totalRuvCount = 0;
    let leakCount = 0;
    let urgencyCount = 0;

    records.forEach((v) => {
      const urineVolume = (v.padUrineVolume ?? 0) + (v.toiletVolume ?? 0);
      totalUrineVolume += urineVolume;
      urineVolume > 0 && totalUrineCount++;
      totalRuv += v.ruv ?? 0;
      v.ruv != null && v.ruv > 0 && totalRuvCount++;
      v.urineLeakage && leakCount++;
      v.isUrgency && urgencyCount++;
    });

    return {
      totalUrineVolume,
      totalUrineCount,
      averageRuv: totalRuv / totalRuvCount || 0,
      totalRuvCount,
      leakCount,
      urgencyCount,
    };
  };

  const { records, recordTotal } = React.useMemo(() => {
    if (!residentCareRecord) {
      return {
        records: [] as ICareRecordTable[],
        recordTotal: {} as ITotalTable | undefined,
      };
    }

    // トイレ排尿無し、おむつ/パッド排尿無し、かつ残尿量未入力は一覧から除く
    const filteredCareRecords = residentCareRecord.filter((v) => {
      return v.urinatedToilet || (v.padUrineVolumeType && v.padUrineVolumeType !== 'none') || v.ruv != null;
    });

    return {
      records: createTableRecord(filteredCareRecords),
      recordTotal: createRecordTotal(filteredCareRecords),
    };
  }, [range.end, range.start, residentCareRecord]);

  const handleClickCSVDownload = async () => {
    if (!range?.start || !range?.end) {
      return;
    }

    setOnSend(true);
    try {
      const residentFullName = resident
        ? `${resident.lastName ?? ''}${resident.name ?? ''}`.trim()
        : undefined;

      const dailyCareRecord = await fetchDailyCareRecord(
        +residentId,
        range.start.local().utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        range.end.local().utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      );

      const exported = await exportCareRecordCsv({
        data: [dailyCareRecord],
        residentFullName,
        start: range.start,
        end: range.end,
      });
      if (!exported) {
        showSnackbar(t('dataForTheSpecifiedPeriodWasNotFound', '指定した期間のデータが見つかりませんでした'));
      }
    } finally {
      setOnSend(false);
    }
  };

  return (
    <div css={Body}>
      <div css={UpperContainer}>
        <div css={DatePickerContainer}>
          <span>{t('startDateAndTime', '開始日時')}: </span>
          <DateTimePicker
            ampm={false}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            value={range.start.local()}
            maxDate={range.end.local()}
            onChange={handleChangeStart}
            renderInput={(params) => <TextField variant={'standard'} {...params} helperText={null} />}
          />
          <span />
          <span>{t('endDateAndTime', '終了日時')}: </span>
          <DateTimePicker
            ampm={false}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            value={range.end.local()}
            minDate={range.start.local()}
            maxDate={dayjs().local().add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0)}
            onChange={handleChangeEnd}
            renderInput={(params) => <TextField variant={'standard'} {...params} helperText={null} />}
          />
        </div>
        <CsvDownloadButton title={t('download', 'ダウンロード')} onClick={handleClickCSVDownload} disable={onSend} />
      </div>
      <div>
        <table css={[TableContainer, SummaryTable]}>
          <tbody>
            <tr>
              <th css={Th(color.bg_light_gray)}>{t('totalUrineVolume', '合計排尿量')}</th>
              <td css={Td()}>{mathRound(recordTotal?.totalUrineVolume ?? 0, 1)}ml</td>
              <th css={Th(color.bg_light_gray)}>{t('totalUrinationCount', '合計排尿回数')}</th>
              <td css={Td()}>
                {recordTotal?.totalUrineCount}
                {t('times', '回')}
              </td>
              <td css={CellGap} />
              <th css={Th(color.bg_light_gray)}>{t('urineLeakageCount', '尿もれ回数')}</th>
              <td css={Td()}>
                {recordTotal?.leakCount}
                {t('times', '回')}
              </td>
              <th css={Th(color.bg_light_gray)}>{t('urgencyCount', '切迫感回数')}</th>
              <td css={Td()}>
                {recordTotal?.urgencyCount}
                {t('times', '回')}
              </td>
            </tr>
            <tr>
              <td css={TableSpacer} colSpan={9} />
            </tr>
            <tr>
              <th css={Th('#E3F5FF')}>{t('averageResidualUrineVolume', '残尿量平均')}</th>
              <td css={Td()}>{mathRound(recordTotal?.averageRuv ?? 0, 1)}ml</td>
              <th css={Th('#E3F5FF')}>{t('residualUrineCount', '残尿回数')}</th>
              <td css={Td()}>
                {recordTotal?.totalRuvCount}
                {t('times', '回')}
              </td>
            </tr>
          </tbody>
        </table>
        <div css={DiaryTableScroll}>
          <table css={DiaryTable}>
            <thead>
              <tr>
                <th css={[Th(color.bg_light_gray), CellWidth[0]]}>{t('diaryDateTime', '排尿時刻')}</th>
                <th css={[Th(color.bg_light_gray), TdCenter, CellWidth[1]]}>{t('urinationVolume', '排尿量')} (ml)</th>
                <th css={[Th('#E3F5FF'), TdCenter, CellWidth[2]]}>{t('residualUrineVolume', '残尿量')} (ml)</th>
                <th css={[Th(color.bg_light_gray), TdCenter, CellWidth[3]]}>{t('urineLevel', 'たまり具合')}</th>
                <th css={[Th(color.bg_light_gray), TdCenter, CellWidth[4]]}>{t('posture', '姿勢')}</th>
                <th css={[Th(color.bg_light_gray), TdCenter, CellWidth[5]]}>{t('leak', '尿もれ')}</th>
                <th css={[Th(color.bg_light_gray), TdCenter, CellWidth[6]]}>{t('isUrgency', '切迫感')}</th>
                <th css={[Th(color.bg_light_gray), CellWidth[7], MemoCell]}>{t('memo', 'メモ')}</th>
                <th css={[Th(color.bg_light_gray), CellWidth[8]]}>{''}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((v) => {
                return (
                  <tr key={v.careRecordId}>
                    <td css={Td()}>{v.timestamp}</td>
                    <td css={[Td(), TdCenter]}>{formatNumberOrBlank(v.urineVolume)}</td>
                    <td css={[Td(), TdCenter]}>{formatNumberOrBlank(v.ruv)}</td>
                    <td css={[Td(), TdCenter]}>{v.accumulation}</td>
                    <td css={[Td(), TdCenter]}>{renderPostureIcon(v.posture)}</td>
                    <td css={[Td(), TdCenter]}>{v.isLeak ? '○' : ''}</td>
                    <td css={[Td(), TdCenter]}>{v.isUrgency ? '○' : ''}</td>
                    <td css={Td()}>{v.memo}</td>
                    <td css={Td()}>
                      <div css={[TableLinkText, TextCenter]} onClick={handleClickEdit(v.careRecordId)}>
                        {t('edit', '編集')}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!records.length && (
                <tr>
                  <td colSpan={9} css={Td()}>
                    {t('noUrinationRecords', '排尿記録はありません')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
